/**
 * src/components/SavePreambleDialog.tsx — "Save Preamble" trigger + dialog + overwrite prompt.
 *
 * Source: `D:/WCS/latex2html-french-trad/src/components/SavePreambleDialog.tsx` (whole file, 282
 * lines) @ `8d01f0c`.
 * Spec: `docs/spec/06-frontend-components.md` §8.2 (every field, string and branch), §7.1/§7.4
 * step 3 (what is persisted), §15 D-8/D-12, §16 ranks 2 and 16.
 *
 * Rendered by `ConverterApp` in the toolbar row (`ConverterApp.tsx:255`), immediately left of
 * `SaveDocumentDialog`. The `triggerButton` prop exists so a caller can supply its own trigger; no
 * shipped call site does — `ConverterApp.tsx:255` passes `preambleContent` and nothing else.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * DF-22 rank 2 — SAME UNREACHABLE UPDATE PATH AS THE DOCUMENT DIALOG
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * `preambleId`, `initialName`, `initialDescription`, `initialIsDefault` and `initialFolderId` are
 * declared here and **never passed**. `ConverterApp.tsx:255` renders exactly:
 *
 *     <SavePreambleDialog preambleContent={preamble} />
 *
 * and `handleLoadPreamble` (`ConverterApp.tsx:125-130`) keeps only the preamble's *name*, never its
 * id. So `preambleId` is always `undefined`, the first branch of {@link performSave} is dead code,
 * and loading a saved preamble, editing it and saving again **creates a second preamble** — or
 * forces the "Preamble Already Exists / Replace" prompt if the name is retyped identically into the
 * same folder. Threading `loadedPreambleId` in is the deferred fix and belongs to `ConverterApp`
 * (CLAUDE.md §3, DEFERRED-FIXES.md DF-22 rank 2). The dead branch is ported, not deleted.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * DF-22 rank 16 — `is_default` IS WRITE-ONLY. THIS FILE IS WHERE IT IS WRITTEN.
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * The `Set as default preamble` checkbox below (`SavePreambleDialog.tsx:225-237`; §15 D-12 cites it
 * as `:227-236`) is the only writer of the flag in the whole application. Nothing reads it back
 * except two cosmetic badges — `Default` in the sidebar (`EnhancedSavedDocuments.tsx:350-354`) and
 * `' (Default)'` in `SaveDocumentDialog.tsx:216`. **No default preamble is ever auto-loaded**, so
 * ticking the box has no effect a user can observe beyond those badges, and starting a new document
 * always leaves the preamble tab empty. There is deliberately no `useDefaultPreamble` hook in
 * `src/hooks/use-database.ts` and no mount effect here. Do not add either.
 *
 * The Worker enforces no uniqueness on the flag (`worker/routes/preambles.ts:24-30`), so any number
 * of preambles can be "default" at once and all of them show the badge. Also reproduced.
 *
 * ═══ OTHER DEFECTS DELIBERATELY REPRODUCED HERE ═══════════════════════════════════════════════
 *  - **§15 D-8 (absent from the DF-22 rank table — see the port report).** The duplicate-name check
 *    is gated on `&& preamblesInFolder` (`:82`); saving while the by-folder query is in flight
 *    skips the overwrite prompt and creates a silent same-name duplicate.
 *  - **DF-22 rank 5.** No error state, no retry. The dialog stays open on failure because
 *    `setOpen(false)` is inside `onSuccess`; the only feedback is `use-database.ts`'s generic
 *    destructive toast.
 *  - **DF-22 rank 9 (the one approved deviation), second half.** A successful create fires TWO
 *    toasts: `'Preamble saved'` from `useCreatePreamble` and `'Preamble saved successfully'` from
 *    `:159` below.
 *  - **Dialog overflow with long LaTeX.** Three separate scroll containers, all from the source's
 *    own `ISSUES_AND_FIXES.md:5-13` fix and all reproduced verbatim: `DialogContent` gets
 *    `sm:max-w-[500px] max-h-[90vh] overflow-y-auto` (`:177`), the inner field grid gets a second
 *    `overflow-y-auto` (`:186`), and the preview `<pre>` gets
 *    `overflow-x-auto overflow-y-auto max-h-[100px] whitespace-pre-wrap break-words` (`:240`). A
 *    500 KB ComTrad preamble therefore neither stretches the dialog nor escapes the viewport — only
 *    its first 200 characters are shown (`:241-242`).
 *  - **No Enter-to-submit** (spec §14 C-8) and no keyboard shortcut (DF-22 rank 15).
 *  - **`description` is sent as `''` rather than omitted** when the field is left blank (`:149`),
 *    so the column stores an empty string, not NULL. Reproduced.
 */
import { useState, type ReactNode } from 'react'
import {
  useCreatePreamble,
  useFolders,
  usePreamblesByFolder,
  useUpdatePreamble,
} from '@/hooks/use-database'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Save } from 'lucide-react'
import { toast } from 'sonner'

/**
 * `SavePreambleDialog.tsx:37-45`, field for field.
 *
 * Everything except `preambleContent` is dead in the shipped app — DF-22 rank 2 for the first five,
 * and `triggerButton` simply has no caller. See the file header.
 */
export interface SavePreambleDialogProps {
  /**
   * **DF-22 rank 1 (owner-approved 2026-08-09).** Called after any successful save, so
   * `ConverterApp` can clear its unsaved-changes flag. Purely additive: it reports, it does not
   * decide, and every existing `onSuccess` behaves exactly as before. Without it the
   * `beforeunload` warning would fire *after* a successful save.
   */
  onSaved?: () => void
  /** The **preamble tab only** — `ConverterApp.tsx:255` passes its `preamble` state. */
  preambleContent: string
  /** DF-22 rank 2 — never passed. Its presence is what would enable the update branch. */
  preambleId?: number
  /** DF-22 rank 2 — never passed. Read once, at mount, into `name`. */
  initialName?: string
  /** DF-22 rank 2 — never passed. */
  initialDescription?: string
  /** DF-22 rank 2 — never passed. */
  initialIsDefault?: boolean
  /** DF-22 rank 2 — never passed. */
  initialFolderId?: number
  /** No shipped caller supplies one; the default outline button below is what always renders. */
  triggerButton?: ReactNode
}

export const SavePreambleDialog = ({
  onSaved,
  preambleContent,
  preambleId,
  initialName = '',
  initialDescription = '',
  initialIsDefault = false,
  initialFolderId,
  triggerButton,
}: SavePreambleDialogProps) => {
  // `SavePreambleDialog.tsx:56-62`. The `initial*` props are read once at mount and never resynced.
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState(initialDescription)
  const [isDefault, setIsDefault] = useState(initialIsDefault)
  const [folderId, setFolderId] = useState<number | undefined>(initialFolderId)
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false)
  const [existingPreambleId, setExistingPreambleId] = useState<number | null>(null)

  // `:64-67`. Note this dialog does NOT call `usePreambles()` — only the by-folder list, because it
  // has no preamble dropdown of its own. `folderId || null` sends `null` for "Root", which
  // `usePreamblesByFolder` maps to the literal path segment `root` (the preambles router rejects
  // `'null'`, unlike documents and folders — `worker/routes/preambles.ts:158-166`).
  const { data: folders } = useFolders()
  const { data: preamblesInFolder } = usePreamblesByFolder(folderId || null)
  const { mutate: createPreamble, isPending: isCreating } = useCreatePreamble()
  const { mutate: updatePreamble, isPending: isUpdating } = useUpdatePreamble()

  /**
   * `:69-95`.
   *
   * Two validity checks, both sonner `toast.error`, both blocking. The second is the reason a
   * whitespace-only preamble tab cannot be saved at all — note it tests `preambleContent`, the
   * prop, not the name field.
   */
  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Please enter a name for the preamble')
      return
    }

    if (!preambleContent.trim()) {
      toast.error('Preamble content cannot be empty')
      return
    }

    // Check for duplicate name in the same folder (only when creating new or changing name)
    // Only check if we have loaded the preambles for the current folder
    //
    // ⚠️ §15 D-8 — `&& preamblesInFolder` is the race. While the by-folder query is in flight the
    // data is `undefined`, the whole check is skipped, and the save creates a silent duplicate.
    // Reproduced deliberately; do not gate the Save button on `isFetching`.
    if ((!preambleId || name !== initialName) && preamblesInFolder) {
      const duplicate = preamblesInFolder.find(
        (p) => p.name.toLowerCase() === name.toLowerCase() && p.id !== preambleId,
      )

      if (duplicate) {
        setExistingPreambleId(duplicate.id)
        setShowOverwriteDialog(true)
        return
      }
    }

    performSave(false)
  }

  /**
   * `:97-164` — three branches, mirroring `SaveDocumentDialog.performSave` exactly.
   *
   * Branch 1 (`preambleId` set) is **unreachable** (DF-22 rank 2) and is the only one that does not
   * reset the form on success (`:112-115`). Like its document twin it ignores `existingPreambleId`,
   * so reached via Replace it would update the *loaded* preamble under the colliding name and still
   * toast `'Preamble replaced successfully'`, leaving the real duplicate in place.
   *
   * Note the name/content/description sent on the update path cannot clear a column: the Worker
   * uses `COALESCE(? || null, col)` for all three (`worker/routes/preambles.ts:280-289`), so an
   * emptied description silently keeps its old text — DF-22 rank 7. `is_default` uses `??`, so an
   * unticked box IS persisted as `false`.
   */
  const performSave = (isOverwrite: boolean) => {
    if (preambleId) {
      // Update existing preamble
      updatePreamble(
        {
          id: preambleId,
          updates: {
            name,
            content: preambleContent,
            description,
            is_default: isDefault,
            folder_id: folderId,
          },
        },
        {
          onSuccess: () => {
            setOpen(false)
            onSaved?.()
            toast.success(
              isOverwrite ? 'Preamble replaced successfully' : 'Preamble updated successfully',
            )
          },
        },
      )
    } else if (isOverwrite && existingPreambleId) {
      // Overwrite existing preamble
      updatePreamble(
        {
          id: existingPreambleId,
          updates: {
            name,
            content: preambleContent,
            description,
            is_default: isDefault,
            folder_id: folderId,
          },
        },
        {
          onSuccess: () => {
            setOpen(false)
            onSaved?.()
            setName('')
            setDescription('')
            setIsDefault(false)
            setFolderId(undefined)
            toast.success('Preamble replaced successfully')
          },
        },
      )
    } else {
      // Create new preamble
      createPreamble(
        {
          name,
          content: preambleContent,
          description,
          is_default: isDefault,
          folder_id: folderId,
        },
        {
          onSuccess: () => {
            setOpen(false)
            onSaved?.()
            setName('')
            setDescription('')
            setIsDefault(false)
            setFolderId(undefined)
            toast.success('Preamble saved successfully')
          },
        },
      )
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {/* `:169-176`. `preambleId` is always undefined ⇒ the label is always 'Save Preamble'. */}
          {triggerButton || (
            <Button variant="outline" className="gap-2">
              <Save className="h-4 w-4" />
              {preambleId ? 'Update Preamble' : 'Save Preamble'}
            </Button>
          )}
        </DialogTrigger>
        {/* `:177` — wider than the document dialog (500 vs 425) to fit the preview block. */}
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{preambleId ? 'Update Preamble' : 'Save Preamble'}</DialogTitle>
            <DialogDescription>
              {preambleId
                ? 'Update your preamble with the latest changes.'
                : 'Save this preamble to reuse it in future documents.'}
            </DialogDescription>
          </DialogHeader>
          {/* `:186` — the second, redundant `overflow-y-auto`. Part of the source's overflow fix. */}
          <div className="grid gap-4 py-4 overflow-y-auto">
            <div className="grid gap-2">
              <Label htmlFor="name">Preamble Name</Label>
              <Input
                id="name"
                placeholder="ComTrad"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              {/* `:197-213`. Identical flat Select to `SaveDocumentDialog` — no hierarchy, and the
                  `id="folder"` collides with that dialog's own folder trigger id (see report). */}
              <Label htmlFor="folder">Folder (Optional)</Label>
              <Select
                value={folderId?.toString() || 'none'}
                onValueChange={(value) => setFolderId(value === 'none' ? undefined : parseInt(value))}
              >
                <SelectTrigger id="folder">
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Root (No folder)</SelectItem>
                  {folders?.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id.toString()}>
                      📁 {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                placeholder=""
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
            {/*
              `:225-237` — DF-22 rank 16. THE ONLY WRITER OF `is_default` IN THE APPLICATION.
              Nothing auto-applies the flag; see the file header. Do not add a "load default on
              mount" effect here or a `useDefaultPreamble` hook in `use-database.ts`.
            */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is-default"
                checked={isDefault}
                onCheckedChange={(checked) => setIsDefault(checked as boolean)}
              />
              <Label htmlFor="is-default" className="text-sm font-normal cursor-pointer">
                Set as default preamble
              </Label>
            </div>
            {/*
              `:238-244` — the preview block. First 200 characters only, with a `...` marker beyond
              that. `substring` counts UTF-16 code units, so a 200th character that is half of a
              surrogate pair is cut mid-pair; irrelevant for the ComTrad preamble (all BMP) but
              noted. The `<pre>` carries its own two scroll axes and a 100 px cap, which is what
              stops a 500 KB preamble from stretching the dialog.
            */}
            <div className="rounded-md bg-muted p-3 max-w-full">
              <p className="text-xs font-medium mb-1">Preview:</p>
              <pre className="text-xs overflow-x-auto overflow-y-auto max-h-[100px] whitespace-pre-wrap break-words">
                {preambleContent.substring(0, 200)}
                {preambleContent.length > 200 ? '...' : ''}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isCreating || isUpdating}>
              {isCreating || isUpdating ? 'Saving...' : preambleId ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* `:257-278`. The prompt every re-save of a loaded preamble walks through — DF-22 rank 2. */}
      <AlertDialog open={showOverwriteDialog} onOpenChange={setShowOverwriteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Preamble Already Exists</AlertDialogTitle>
            <AlertDialogDescription>
              A preamble with the name "{name}" already exists in this folder. Do you want to replace
              it?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowOverwriteDialog(false)
                performSave(true)
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
